import { Backdrop, CircularProgress, Typography } from '@mui/material';
import UPCheckbox from 'components/app-container/checkbox-with-label';
import UPButton from 'components/button';
import { DeleteModalTitle, ModalActionContainer } from 'components/delete-modal/styles';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_MERGE__CONFIRM_BUTTON,
  CONTRACT_MERGE__CONFIRM_TITLE,
  CONTRACT_MERGE__DIMONA_IMPACT,
  CONTRACT_MERGE__DIMONA_WARNING,
  CONTRACT_MERGE__IN_PROGRESS,
  CONTRACT_MERGE__LOADING,
  CONTRACT_MERGE__PLANNING_SYSTEM_NOTE,
  CONTRACT_MERGE__REASON_UNKNOWN,
  CONTRACT_MERGE__RESULT_MERGED,
  CONTRACT_MERGE__RESULT_NOT_MERGED,
  CONTRACT_MERGE__RESULT_TITLE,
  CONTRACT_MERGE__TOTAL_HOURS,
  GENERAL__CANCEL_BUTTON,
  GENERAL__CLOSE,
} from 'translations/constants';
import { IContractMergeMessage, IContractMergeProposal } from 'types/contract/IContractMerge';
import dayjs from 'utils/dayjs';
import { fillPlaceholders } from 'utils/fill-placeholders';
import { getWeekDaysAbbreviations } from 'utils/helpers';
import { translateValidationMessage } from 'utils/translateValidationMessage';
import {
  MergeDayRow,
  MergeModalContainer,
  MergeModalStyled,
  MergeNote,
  MergeProgress,
  MergeProposalCard,
  MergeProposalDays,
  MergeResultMessage,
  MergeResultTitle,
  MergeWarning,
} from './styles';
import useContractMerge, { isMergedRow } from './useContractMerge';

type ContractMergeState = ReturnType<typeof useContractMerge>;

interface ContractMergeModalProps {
  merge: ContractMergeState;
}

/** The weekday comes from the translated abbreviations of the planning grid (dayjs has no locale loaded). */
const formatDate = (date: string): string => {
  const day = dayjs(date);
  return `${getWeekDaysAbbreviations()[(day.day() + 6) % 7]} ${day.format('DD/MM')}`;
};

const formatHours = (hours: number): string => {
  const totalMinutes = Math.round(hours * 60);
  return `${Math.floor(totalMinutes / 60)}:${String(totalMinutes % 60).padStart(2, '0')}`;
};

const formatPeriod = (proposal: IContractMergeProposal): string =>
  `${formatDate(proposal.startDate)} - ${formatDate(proposal.endDate)}`;

/**
 * One reason of a result as a line of text: the day it is about and the translated reason. Only translated wording
 * reaches the screen: the text UPay wrote (in the language of its own login) and technical errors stay out. A reason
 * UPortal has no wording for gets UPay's code, which is a number and the same in every language, for support.
 */
const describeMessage = (t: TFunction, message: IContractMergeMessage, proposal: IContractMergeProposal): string => {
  const day = proposal.days.find(d => d.contractDateId === message.contractDateId);
  const isUnknown = message.translationKey === CONTRACT_MERGE__REASON_UNKNOWN;
  const detail = isUnknown ? message.code : '';

  return [
    day ? formatDate(day.date) : '',
    translateValidationMessage(t, message.translationKey),
    detail ? `(${detail})` : '',
  ]
    .filter(Boolean)
    .join(' ');
};

// The texts of this module are backend-managed with {{Name}} placeholders. The web's i18next is configured for single
// braces (translations/index.ts), so t() would leave them in the text: they are filled with fillPlaceholders.
const Proposals = ({ merge }: ContractMergeModalProps): JSX.Element => {
  const [t] = useTranslation();

  return (
    <>
      {merge.proposals.map(proposal => (
        <MergeProposalCard key={proposal.key}>
          <UPCheckbox
            label={
              <Typography fontWeight="bold">
                {proposal.employeeName} - {proposal.companyName}
              </Typography>
            }
            value={merge.selectedKeys.includes(proposal.key)}
            handleChange={() => merge.toggle(proposal.key)}
          />
          <MergeProposalDays>
            <Typography fontSize={14} fontWeight={600}>
              {formatPeriod(proposal)}
            </Typography>
            {proposal.days.map(day => (
              <MergeDayRow key={day.contractDateId}>
                <span>{formatDate(day.date)}</span>
                <span>
                  {day.startTime} - {day.endTime} ({formatHours(day.hours)})
                </span>
              </MergeDayRow>
            ))}
            <MergeDayRow>
              <strong>{t(CONTRACT_MERGE__TOTAL_HOURS)}</strong>
              <strong>{formatHours(proposal.totalHours)}</strong>
            </MergeDayRow>
          </MergeProposalDays>
          {proposal.dimonaImpactDates.length > 0 && (
            <MergeWarning>
              {fillPlaceholders(t(CONTRACT_MERGE__DIMONA_IMPACT), {
                dates: proposal.dimonaImpactDates.map(formatDate).join(', '),
              })}
            </MergeWarning>
          )}
        </MergeProposalCard>
      ))}
      <MergeNote>{t(CONTRACT_MERGE__PLANNING_SYSTEM_NOTE)}</MergeNote>
    </>
  );
};

const Results = ({ merge }: ContractMergeModalProps): JSX.Element => {
  const [t] = useTranslation();

  return (
    <>
      {merge.rows.map(row => (
        <MergeProposalCard key={row.proposal.key}>
          <MergeResultTitle>
            {row.proposal.employeeName} - {formatPeriod(row.proposal)}:{' '}
            {isMergedRow(row) ? t(CONTRACT_MERGE__RESULT_MERGED) : t(CONTRACT_MERGE__RESULT_NOT_MERGED)}
          </MergeResultTitle>
          {!isMergedRow(row) &&
            row.result.messages.map(message => (
              <MergeResultMessage key={`${message.contractDateId ?? 0}_${message.code ?? message.translationKey}`}>
                {describeMessage(t, message, row.proposal)}
              </MergeResultMessage>
            ))}
          {row.result.dimonaWarning && <MergeResultMessage>{t(CONTRACT_MERGE__DIMONA_WARNING)}</MergeResultMessage>}
        </MergeProposalCard>
      ))}
    </>
  );
};

/**
 * The confirmation, the wait and the outcome of merging sequential daily contracts. It is one dialog that changes with
 * the phase of the merge, so the SuperAdmin never has more than one thing to look at.
 */
const ContractMergeModal = ({ merge }: ContractMergeModalProps): JSX.Element => {
  const [t] = useTranslation();
  const { phase } = merge;

  if (phase === 'closed') {
    return null;
  }

  const isBusy = phase === 'loading' || phase === 'merging';

  const renderBody = () => {
    switch (phase) {
      case 'loading':
        return (
          <MergeProgress>
            <CircularProgress size={40} />
            <Typography>{t(CONTRACT_MERGE__LOADING)}</Typography>
          </MergeProgress>
        );
      case 'merging':
        return (
          <MergeProgress>
            <CircularProgress size={40} />
            <Typography>{t(CONTRACT_MERGE__IN_PROGRESS)}</Typography>
          </MergeProgress>
        );
      case 'notice':
        return <Typography textAlign="center">{t(merge.noticeKey)}</Typography>;
      case 'results':
        return <Results merge={merge} />;
      default:
        return <Proposals merge={merge} />;
    }
  };

  return (
    <Backdrop open sx={{ zIndex: '1000' }}>
      <MergeModalStyled open onClose={isBusy ? undefined : merge.close}>
        <MergeModalContainer>
          <DeleteModalTitle>
            {phase === 'results' ? t(CONTRACT_MERGE__RESULT_TITLE) : t(CONTRACT_MERGE__CONFIRM_TITLE)}
          </DeleteModalTitle>
          {renderBody()}
          {!isBusy && (
            <ModalActionContainer>
              {phase === 'review' && (
                <>
                  <UPButton
                    text={t(CONTRACT_MERGE__CONFIRM_BUTTON)}
                    onClick={merge.confirm}
                    disabled={merge.selectedKeys.length === 0}
                  />
                  <UPButton text={t(GENERAL__CANCEL_BUTTON)} outlined onClick={merge.close} />
                </>
              )}
              {phase !== 'review' && <UPButton text={t(GENERAL__CLOSE)} outlined onClick={merge.close} />}
            </ModalActionContainer>
          )}
        </MergeModalContainer>
      </MergeModalStyled>
    </Backdrop>
  );
};

export default ContractMergeModal;
